import { Component, ElementRef, EventEmitter, HostListener, inject, Input, OnInit, Output } from '@angular/core';
import { NavMenuItem } from '@core/types';
import { mobileSidebarMenuItems } from '@core/constants';
import { NavigationEnd, Router } from '@angular/router';
import { ShellService } from '@app/shell/services/shell.service';
import { CredentialsService, PermissionService } from '@auth';
import { Credentials } from '@core/entities';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs/operators';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-side-navigation',
  templateUrl: './side-navigation.component.html',
  styleUrls: ['./side-navigation.component.scss'],
  standalone: false,
})
export class SideNavigationComponent implements OnInit {
  shellService = inject(ShellService);
  @Input() isSidebarActive: boolean;
  userDetail: Credentials = null;
  @Output() toggleSideBar = new EventEmitter<boolean>();
  sidebarItems: NavMenuItem[] = [];
  sidebarExtendedItem = -1;
  private readonly _router = inject(Router);
  private readonly _eRef = inject(ElementRef);
  private readonly _credentialService = inject(CredentialsService);
  private readonly _permissionService = inject(PermissionService);

  constructor() {
    this.sidebarItems = mobileSidebarMenuItems;
  }

  get clubDetails(): any {
    const credentials = this._credentialService.credentials;

    if (!credentials || !credentials.club) {
      window.location.href = '/logout';
    }

    return credentials?.club;
  }

  private get _userRole(): any {
    return this._permissionService.userRole;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    // Check if the clicked element is not the sidebar toggle button
    if (!this._eRef.nativeElement.contains(event.target) && !(event.target as HTMLElement).closest('#mobileSideMenuIcon')) {
      this.isSidebarActive = false;
      this.toggleSideBar.emit(false);
    }
  }

  ngOnInit() {
    this.getUserDetails();

    // Initialize active tab state
    this.shellService.activeNavTab(this.sidebarItems);

    // Listen to router events to update active state
    this._router.events
      .pipe(untilDestroyed(this))
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe({
        next: () => {
          this.shellService.activeNavTab(this.sidebarItems);
        },
      });

    // update sideBar subitems in of item href:'/m/club/add-ons'
    const clubAddOnsIndex = this.sidebarItems.findIndex((item) => item.href === '/m/club/add-ons');
    if (clubAddOnsIndex > -1) {
      this.sidebarItems[clubAddOnsIndex].subItems = [
        { url: this.clubDetails?.linkToVV || '', icon: 'user', title: 'Club Administration' },
        { url: this.clubDetails?.linkToFIBU || '', icon: 'receipt', title: 'Financial Accounting' },
        { url: 'https://admin.verein.cloud/', icon: 'user-id-outline', title: 'License & More' },
      ];
    }
  }

  getUserDetails() {
    this.userDetail = this._credentialService.credentials;
  }

  activateSidebarItem(index: number): void {
    if (index !== this.sidebarExtendedItem) {
      this.sidebarExtendedItem = index;
    } else {
      this.sidebarExtendedItem = -1; // Toggle the same item
    }

    const item = this.sidebarItems[index];
    if (item.disabled) return;

    // Use ShellService to properly activate the item
    this.shellService.activateNavItem(index, this.sidebarItems);

    if (item && (!item.subItems || !item.subItems.length)) {
      // Close the sidebar only if there are no sub-items
      if (!item.subItems || item.subItems.length === 0) {
        this.isSidebarActive = false;
        this.toggleSideBar.emit(false);
      }
    }
  }

  onSubMenuClick(index: number, subItem?: NavMenuItem): void {
    if (subItem) {
      // Use ShellService to activate sub-item properly
      this.shellService.activateNavSubItem(index, subItem, this.sidebarItems);
    } else {
      this.activateSidebarItem(index);
    }
    this.isSidebarActive = false;
    this.toggleSideBar.emit(false);
  }
}
